import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/customer-ui/components/input-group";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircleIcon,
  AtSignIcon,
  GiftIcon,
  LoaderIcon,
  PhoneIcon,
  UserIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useCustomText } from "../hooks/use-custom-text";

const schema = z.object({
  giftCode: z.string(),
  last_name: z.string().min(1),
  first_name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

export function GiftIdentificationPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("gift-return-token") ?? undefined;
  const t = useCustomText();

  const { control, handleSubmit, formState: { isSubmitting, errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { giftCode: "", last_name: "", first_name: "", email: "", phone: "" },
  });

  const onSubmit = (values: FormValues) => {
    navigate("address", {
      state: {
        token,
        giftCode: values.giftCode || undefined,
        last_name: values.last_name,
        first_name: values.first_name,
        email: values.email,
        phone: values.phone,
      },
    });
  };

  return (
    <WidgetPage variant="centered">
      <Header>
        <HeaderTitle>{t("gift.identification.title")}</HeaderTitle>
        <HeaderDescription>{t("gift.identification.description")}</HeaderDescription>
      </Header>

      {Object.keys(errors).length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            {[errors.giftCode, errors.last_name, errors.first_name, errors.email, errors.phone]
              .filter(Boolean)
              .map((e, i) => <div key={i}>{e!.message}</div>)}
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col gap-3"
        >
          {!token && (
            <Controller
              control={control}
              name="giftCode"
              render={({ field, fieldState }) => (
                <InputGroup className="h-auto">
                  <InputGroupInput placeholder="GIFT-XXXX" {...field} aria-invalid={!!fieldState.error} />
                  <InputGroupAddon align="block-start">
                    <InputGroupText>{t("gift.identification.giftCode")}</InputGroupText>
                    <GiftIcon />
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Controller
              control={control}
              name="last_name"
              render={({ field, fieldState }) => (
                <InputGroup className="h-auto">
                  <InputGroupInput placeholder={t("common.fields.lastNameShort")} {...field} aria-invalid={!!fieldState.error} />
                  <InputGroupAddon align="block-start">
                    <InputGroupText>{t("common.fields.lastName")}</InputGroupText>
                    <UserIcon />
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
            <Controller
              control={control}
              name="first_name"
              render={({ field, fieldState }) => (
                <InputGroup className="h-auto">
                  <InputGroupInput placeholder={t("common.fields.firstName")} {...field} aria-invalid={!!fieldState.error} />
                  <InputGroupAddon align="block-start">
                    <InputGroupText>{t("common.fields.firstName")}</InputGroupText>
                    <UserIcon />
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
          </div>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <InputGroup className="h-auto">
                <InputGroupInput type="email" placeholder="email@example.com" {...field} aria-invalid={!!fieldState.error} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.email")}</InputGroupText>
                  <AtSignIcon />
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <InputGroup className="h-auto">
                <InputGroupInput type="tel" placeholder="+33 6 00 00 00 00" {...field} aria-invalid={!!fieldState.error} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.phone")}</InputGroupText>
                  <PhoneIcon />
                </InputGroupAddon>
              </InputGroup>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <LoaderIcon className="animate-spin" /> : t("common.continue")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>

      <button
        type="button"
        className="font-content text-sm text-muted-foreground hover:text-foreground underline mt-2 text-center"
        onClick={() => navigate(`/return-widget/${shopId}`)}
      >
        {t("gift.notAGift")}
      </button>
    </WidgetPage>
  );
}
